import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const navItems = [
  { to: "/shop", label: "Boutique" },
  { to: "/healthy-guides", label: "Healthy Guides" },
  { to: "/kitchen-corner", label: "Kitchen Corner" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 group" data-testid="header-logo">
          <div className="w-9 h-9 rounded-full bg-[#7AC143] flex items-center justify-center text-white font-black text-lg group-hover:bg-[#67BC2A] transition-colors">H</div>
          <span className="font-black text-lg tracking-tight text-[#1A2E1A]">Herbalife<span className="text-[#7AC143]">Shop</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors ${isActive ? "text-[#7AC143]" : "text-[#1A2E1A] hover:text-[#7AC143]"}`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpen(true)}
            className="relative p-2 rounded-full hover:bg-[#F2F5F0] transition-colors"
            data-testid="header-cart-button"
            aria-label="Panier"
          >
            <ShoppingBag className="w-5 h-5 text-[#1A2E1A]" />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#7AC143] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-user-menu" className="p-2 rounded-full hover:bg-[#F2F5F0] transition-colors">
                  <User className="w-5 h-5 text-[#1A2E1A]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-semibold">{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(user.role === "admin" || user.role === "assistant") && (
                  <DropdownMenuItem
                    onClick={() => navigate(user.role === "admin" ? "/admin" : "/assistant")}
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Tableau de bord
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/mes-adresses")} data-testid="menu-addresses">
                  <MapPin className="w-4 h-4 mr-2" />
                  Mes adresses
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/auth")}
              data-testid="header-login-btn"
              className="bg-[#7AC143] hover:bg-[#67BC2A] text-white rounded-full px-5 font-semibold"
            >
              Connexion
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="md:hidden p-2 rounded-full hover:bg-[#F2F5F0]" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {navItems.map((it) => (
                <DropdownMenuItem key={it.to} onClick={() => navigate(it.to)}>
                  {it.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
